import { css } from "@emotion/css";
import { ErrorPanel } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery, useMutation } from "@octopusdeploy/octopus-react-client";
import type { Repository } from "@octopusdeploy/octopus-server-client";
import { Permission, TaskName, TaskState } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import BuiltInRepositoryIndexing from "~/areas/library/components/BuiltInRepository/BuiltInRepositoryIndexing";
import BuiltInRepositoryRetention from "~/areas/library/components/BuiltInRepository/BuiltInRepositoryRetention";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { Skeleton } from "~/components/Skeleton/Skeleton";
export type BuiltInRepositoryPageSidebarProps = {};
export function BuiltInRepositoryPageSidebar(_: BuiltInRepositoryPageSidebarProps) {
    const navigate = useSpaceAwareNavigation();
    const { result, isLoading, refetch, error: queryError, } = useInlineStatusQuery(async (repository) => ({
        builtInFeed: await repository.Feeds.getBuiltIn(),
        syncStatus: isAllowed({ permission: Permission.TaskView }) ? await getLastRepositorySync(repository) : null,
        totalPackages: (await repository.Feeds.getBuiltInStatus()).TotalPackages,
    }), [], "Built In Repository feed and stats query");
    const { execute: updateFeedSync, isExecuting: isExecutingFeedSync } = useMutation({
        name: "Update feed sync status",
        action: async (repository: Repository, enabled: boolean) => {
            if (result?.builtInFeed) {
                return await repository.Feeds.modify({ ...result.builtInFeed, IsBuiltInRepoSyncEnabled: enabled });
            }
        },
        afterComplete: async () => refetch(),
    });
    const { execute: reindexFeed, isExecuting: isExecutingReindex } = useMutation({
        name: "Reindex built in repository",
        action: async (repository) => await repository.Tasks.createSynchronizeBuiltInPackageRepositoryTask(),
        afterComplete: async (_, task) => {
            navigate.navigate(links.taskPage.generateUrl({ taskId: task.Id }));
        },
    });
    if (queryError) {
        return <ErrorPanel error={createErrorInfoFromError(queryError)}/>;
    }
    if (!result) {
        return <SidebarSkeleton />;
    }
    const { builtInFeed, syncStatus, totalPackages } = result;
    return (<>
            <BuiltInRepositoryRetention config={builtInFeed} afterConfigurationUpdated={(_) => refetch()}/>
            <hr />
            <BuiltInRepositoryIndexing isBuiltInRepoSyncEnabled={builtInFeed.IsBuiltInRepoSyncEnabled} lastSyncStatus={syncStatus?.status} lastSyncId={syncStatus?.id} lastSyncCompletedTime={syncStatus?.completedTime ?? undefined} totalPackages={totalPackages} onReindexClick={reindexFeed} setIsBuiltInRepoSyncEnabled={updateFeedSync} busy={isLoading || isExecutingFeedSync || isExecutingReindex}/>
        </>);
}
async function getLastRepositorySync(repository: Repository) {
    const useOptimization = isFeatureToggleEnabled("PermissionCheckOptimizationFeatureToggle");
    const tasks = useOptimization ? await repository.Tasks.getUnpaginatedTasks({ name: TaskName.SynchronizeBuiltInPackageRepositoryIndex, take: 1 }) : await repository.Tasks.list({ name: TaskName.SynchronizeBuiltInPackageRepositoryIndex, take: 1 });
    if (tasks.Items.length === 0) {
        return { status: "Never synced" };
    }
    const task = tasks.Items[0];
    switch (task.State) {
        case TaskState.Success:
            return { id: task.Id, status: "Succeeded", completedTime: task.CompletedTime };
        case TaskState.Executing:
            return { id: task.Id, status: "Running", completedTime: task.CompletedTime };
        default:
            return { id: task.Id, status: "Failed", completedTime: task.CompletedTime };
    }
}
function SidebarSkeleton() {
    return (<div className={sidebarSkeletonStyles}>
            <SidebarSkeletonSection />
            <SidebarSkeletonSection />
        </div>);
}
const sidebarSkeletonStyles = css({
    padding: `${space[16]} 0`,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    rowGap: space[16],
});
function SidebarSkeletonSection() {
    return <Skeleton shape={"Rounded"} borderRadius={"large"}/>;
}
